const encoder = new TextEncoder();
const decoder = new TextDecoder();

export const RealtimeEvent = {
  StartConnection: 1,
  FinishConnection: 2,
  ConnectionStarted: 50,
  ConnectionFailed: 51,
  StartSession: 100,
  FinishSession: 102,
  SessionStarted: 150,
  SessionFinished: 152,
  SessionFailed: 153,
  TaskRequest: 200,
  EndASR: 400,
  ASRInfo: 450,
  ASRResponse: 451,
  ASREnded: 459,
  ChatTextQuery: 501,
  TTSResponse: 352,
  TTSEnded: 359,
  ChatResponse: 550,
  ChatTextQueryConfirmed: 553,
  ChatEnded: 559,
} as const;

export type DecodedRealtimeFrame = {
  messageType: number;
  flags: number;
  event?: number;
  sessionId?: string;
  code?: number;
  payload: Uint8Array;
  json?: Record<string, unknown>;
};

function writeUint32(view: DataView, offset: number, value: number): number {
  view.setUint32(offset, value, false);
  return offset + 4;
}

export function encodeRealtimeEvent(event: number, payload: Record<string, unknown> | Uint8Array, sessionId?: string): ArrayBuffer {
  const raw = payload instanceof Uint8Array;
  const payloadBytes = raw ? payload : encoder.encode(JSON.stringify(payload));
  const sessionBytes = sessionId ? encoder.encode(sessionId) : new Uint8Array();
  const includeSession = event >= 100;
  const size = 4 + 4 + (includeSession ? 4 + sessionBytes.length : 0) + 4 + payloadBytes.length;
  const buffer = new ArrayBuffer(size);
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  bytes[0] = 0x11;
  bytes[1] = raw ? 0x24 : 0x14;
  bytes[2] = raw ? 0x00 : 0x10;
  bytes[3] = 0x00;
  let offset = 4;
  offset = writeUint32(view, offset, event);
  if (includeSession) {
    offset = writeUint32(view, offset, sessionBytes.length);
    bytes.set(sessionBytes, offset);
    offset += sessionBytes.length;
  }
  offset = writeUint32(view, offset, payloadBytes.length);
  bytes.set(payloadBytes, offset);
  return buffer;
}

export function decodeRealtimeFrame(buffer: ArrayBuffer): DecodedRealtimeFrame {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  if (bytes.length < 8 || (bytes[0]! >> 4) !== 1) throw new Error("无效的实时语音协议帧");
  const headerSize = (bytes[0]! & 0x0f) * 4;
  const messageType = bytes[1]! >> 4;
  const flags = bytes[1]! & 0x0f;
  const serialization = bytes[2]! >> 4;
  let offset = headerSize;
  let code: number | undefined;
  let event: number | undefined;
  let sessionId: string | undefined;

  if (messageType === 0x0f) {
    code = view.getUint32(offset, false);
    offset += 4;
  }
  if ((flags & 0x01) !== 0) offset += 4;
  if ((flags & 0x04) !== 0) {
    event = view.getUint32(offset, false);
    offset += 4;
  }
  if (event !== undefined && event >= 100) {
    const sessionLength = view.getUint32(offset, false);
    offset += 4;
    sessionId = decoder.decode(bytes.slice(offset, offset + sessionLength));
    offset += sessionLength;
  }
  const payloadLength = offset + 4 <= bytes.length ? view.getUint32(offset, false) : 0;
  offset += 4;
  const payload = bytes.slice(offset, Math.min(bytes.length, offset + payloadLength));
  let json: Record<string, unknown> | undefined;
  if (serialization === 1 && payload.length) {
    try { json = JSON.parse(decoder.decode(payload)) as Record<string, unknown>; } catch { /* raw provider detail */ }
  }
  return { messageType, flags, event, sessionId, code, payload, json };
}
